import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApiRequest from '../../../../lib/hooks/useRequest';
import useVerified from '../../../../lib/hooks/useVerified';
import { SelfCheckoutCodeType } from '../../../../lib/interfaces/organisation';
import BgStickyGradient from '../../../../ui/flow-components/bg-sticky-gradient';
import { CheckoutSkeleton } from './components/checkout-skeleton';
import PaymentFormFetcher from './components/payment-form';
import { TierCard } from './components/tier-card';
import { mapBackendPackagesToDisplayConfig, PackageDisplayConfig } from './config/package-mapper';
import { BillingFrequency } from './config/packages-display.config';
import { GOLD_BADGE_TEXT, GOLD_FIRST_MONTH_DISCOUNT } from './config/self-checkout-codes.config';
import { PackageCategory } from './enums/package-category.enum';
import { CapacityMetaResponseDto, SelfCheckoutMetaResponseDto } from './types/response';

const OnboardingPaymentStep = () => {
  const { checkVerification } = useVerified();
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();

  const [packages, setPackages] = useState<Record<PackageCategory, PackageDisplayConfig>>();
  const [capacities, setCapacities] = useState<CapacityMetaResponseDto>();
  const [selectedPlan, setSelectedPlan] = useState<string>();
  const [selectedTier, setSelectedTier] = useState<PackageCategory>();
  const [selectedBillingFrequency, setSelectedBillingFrequency] = useState<BillingFrequency>(BillingFrequency.MONTHLY);
  const [activeCodeType, setActiveCodeType] = useState<SelfCheckoutCodeType>();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const personalData = localStorage.getItem('selfCheckoutPersonalData');
    if (!personalData) {
      checkVerification();
    }
  }, [navigate, checkVerification]);

  const getPackages = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<SelfCheckoutMetaResponseDto>('self-checkout/meta', 'GET');
    if (response.data) {
      const mappedPackages = mapBackendPackagesToDisplayConfig(response.data.packages);
      setPackages(mappedPackages);
      setCapacities(response.data.capacities);
      setSelectedTier(PackageCategory.PRO);
      setIsLoading(false);
    } else {
      console.error('Failed to get packages', response.error);
    }
  }, [apiRequest]);

  useEffect(() => {
    getPackages();
  }, [getPackages]);

  useEffect(() => {
    console.log('selectedTier', selectedTier);
    console.log('selectedBillingFrequency', selectedBillingFrequency);
    console.log('packages', packages);
    if (packages && selectedTier && selectedBillingFrequency) {
      setSelectedPlan(packages[selectedTier][selectedBillingFrequency].id);
    }
  }, [selectedTier, selectedBillingFrequency, packages]);

  if (isLoading || !packages || !capacities || !selectedPlan) {
    return <CheckoutSkeleton />;
  }

  // GOLD rabattiert einmalig den ersten Monat - das Paket selbst bleibt unverändert,
  // deshalb nur Badge + Erster-Monat-Zeile statt eines geänderten Monatspreises.
  const isGoldActive = activeCodeType === SelfCheckoutCodeType.GOLD;
  const goldFirstMonthPrice = (): string | undefined => (isGoldActive ? Math.max(0, GOLD_FIRST_MONTH_DISCOUNT).toLocaleString('de-DE') : undefined);

  return (
    <div className="min-h-screen relative w-screen bg-[#000f0f] overflow-hidden">
      <BgStickyGradient />

      <div className="flex flex-col lg:flex-row w-full min-h-screen relative z-10">
        {/* Plans Section */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center lg:p-0">
          <div className="p-4 lg:p-20 flex flex-col gap-8 w-full max-w-5xl">
            {/* Billing Frequency Switch */}
            {/*<div className="w-full flex justify-center">
              <BillingFrequencySwitch selectedFrequency={selectedBillingFrequency} onFrequencyChange={setSelectedBillingFrequency} />
            </div>*/}

            {/* Package Cards */}
            {packages && (
              <div className="w-full flex flex-row">
                {selectedTier === PackageCategory.LITE && packages[PackageCategory.LITE] ? (
                  <div className="w-full flex justify-center">
                    <div className="w-full max-w-md">
                      <TierCard
                        packageCategory={PackageCategory.LITE}
                        setTier={setSelectedTier}
                        isSelected={true}
                        selectedBillingFrequency={selectedBillingFrequency}
                        packageDisplayConfig={packages[PackageCategory.LITE]}
                        originalPriceDisplay={packages[PackageCategory.BASIC]?.[selectedBillingFrequency]?.price}
                        unlockBadgeText="LITE-Code aktiv"
                        widthClassName="w-full"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="w-full flex flex-col lg:flex-row gap-6 items-start">
                    <TierCard
                      packageCategory={PackageCategory.BASIC}
                      setTier={setSelectedTier}
                      isSelected={selectedTier === PackageCategory.BASIC}
                      selectedBillingFrequency={selectedBillingFrequency}
                      packageDisplayConfig={packages[PackageCategory.BASIC]}
                      unlockBadgeText={isGoldActive ? GOLD_BADGE_TEXT : undefined}
                      unlockBadgeVariant="gold"
                      firstMonthPriceDisplay={goldFirstMonthPrice()}
                    />
                    <TierCard
                      isSelected={selectedTier === PackageCategory.PRO}
                      selectedBillingFrequency={selectedBillingFrequency}
                      packageCategory={PackageCategory.PRO}
                      setTier={setSelectedTier}
                      packageDisplayConfig={packages[PackageCategory.PRO]}
                      unlockBadgeText={isGoldActive ? GOLD_BADGE_TEXT : undefined}
                      unlockBadgeVariant="gold"
                      firstMonthPriceDisplay={goldFirstMonthPrice()}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Form Section */}
        <div className="w-full lg:w-1/3 p-4 lg:pr-16 pr-4 flex items-center lg:items-start lg:h-screen lg:overflow-y-auto lg:py-8">
          <div className="bg-white rounded-xl p-6 lg:p-8 w-full">
            <PaymentFormFetcher
              capacities={capacities}
              packages={packages}
              selectedPlan={selectedPlan}
              selectedBillingFrequency={selectedBillingFrequency}
              setSelectedBillingFrequency={setSelectedBillingFrequency}
              selectedTier={selectedTier!}
              setSelectedTier={setSelectedTier}
              setActiveCodeType={setActiveCodeType}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPaymentStep;
